import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Crosshair2Icon } from "@radix-ui/react-icons";
import { useEditor, useValue } from "@tldraw/editor";
import { ChevronsDownIcon, ChevronsUpIcon } from "lucide-react";
import { memo, useCallback, useRef } from "react";
import {
  DefaultZoomMenuContent,
  TldrawUiMenuContextProvider,
  useLocalStorageState,
  usePassThroughWheelEvents,
  useTldrawUiComponents,
} from "tldraw";

import { preventBubble } from "@/components/canvas-editor/utils";
import { Button } from "@/components/ui/button";
import { NumberInput } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export const Minimap = memo(function Minimap() {
  const ref = useRef<HTMLDivElement>(null!);
  usePassThroughWheelEvents(ref);

  const [collapsed, setCollapsed] = useLocalStorageState("minimap", true);

  const toggleMinimap = useCallback(() => {
    setCollapsed((s) => !s);
  }, [setCollapsed]);

  const TLDraw = useTldrawUiComponents();
  const editor = useEditor();
  const zoom = useValue("zoom", () => editor.getZoomLevel(), [editor]);

  const input = (
    <NumberInput
      aria-label="Zoom level"
      size="small"
      value={zoom}
      onValueChange={(v) =>
        editor.setCamera({
          ...editor.getCamera(),
          z: v,
        })
      }
      className="w-28 text-right"
      min={0.01}
      step={0.01}
      formatOptions={{
        style: "percent",
      }}
    />
  );

  const menu = (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button size="smIcon" variant="ghost">
          <span className="sr-only">Zoom and camera options</span>
          <Crosshair2Icon />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className="tlui-menu"
        side="top"
        align="start"
        alignOffset={0}
        sideOffset={8}
        collisionPadding={4}
      >
        <TldrawUiMenuContextProvider type="menu" sourceId="zoom-menu">
          <DefaultZoomMenuContent />
        </TldrawUiMenuContextProvider>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );

  return (
    <div
      ref={ref}
      {...preventBubble}
      className={cn(
        "absolute bottom-0 left-0 border-t border-r border-slate-dim rounded-tr-lg bg-slate-app p-2 flex flex-col gap-2 pointer-events-auto",
        "after:content-[''] after:absolute after:inset-0 after:bg-slate-2 dark:after:bg-slatedark-2 after:opacity-50 hover:after:opacity-0 focus-within:after:opacity-0 after:transition-opacity after:duration-200 after:pointer-events-none",
      )}
    >
      <div className="tlui-buttons__horizontal flex items-center gap-2">
        {input}
        {menu}
        {collapsed ? (
          <Button size="smIcon" variant="ghost" onClick={toggleMinimap}>
            <span className="sr-only">Show minimap</span>
            <ChevronsUpIcon />
          </Button>
        ) : (
          <Button size="smIcon" variant="ghost" onClick={toggleMinimap}>
            <span className="sr-only">Hide minimap</span>
            <ChevronsDownIcon />
          </Button>
        )}
      </div>
      {TLDraw.Minimap && !collapsed && (
        <div className="rounded-lg overflow-hidden border border-slate-5 dark:border-slatedark-5">
          <TLDraw.Minimap />
        </div>
      )}
    </div>
  );
});
